import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { tokenStorage } from './tokenStorage';
import { createDebugLogger } from '../utils/debug';

const dlog = createDebugLogger('auth');

const AuthContext = createContext(null);

// Synchroner Boot-Read — vermeidet setState-in-useEffect-Pattern.
// Läuft genau einmal pro Tab beim Modul-Load.
const bootTokens = (() => {
  if (typeof window === 'undefined') {
    return { accessToken: null, refreshToken: null };
  }
  return tokenStorage.read();
})();

/**
 * Auth-State-Machine: 'anonymous' | 'authenticated' | 'mfa-pending'
 * Tokens leben in Refs (kein Re-Render auf Refresh).
 *
 * Nativer Login: Email/Password + MFA gegen Core (/v1/auth/*). Operatoren
 * (Rolle admin/staff) melden sich genau wie Endkunden an. Die frühere
 * Zitadel-OIDC-Föderation wurde ersatzlos entfernt.
 */
export function AuthProvider({ children }) {
  const accessRef = useRef(bootTokens.accessToken);
  const refreshRef = useRef(bootTokens.refreshToken);

  const [status, setStatus] = useState(
    bootTokens.accessToken ? 'authenticated' : 'anonymous',
  );
  const [mfaChallenge, setMfaChallenge] = useState(null);

  // Cross-Tab-Sync via storage-Event.
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== tokenStorage.keys.access) return;
      const t = tokenStorage.read();
      accessRef.current = t.accessToken;
      refreshRef.current = t.refreshToken;
      setStatus(t.accessToken ? 'authenticated' : 'anonymous');
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setTokens = useCallback((t) => {
    accessRef.current = t.accessToken;
    refreshRef.current = t.refreshToken;
    tokenStorage.write(t);
    setMfaChallenge(null);
    setStatus('authenticated');
  }, []);

  const beginMfa = useCallback((challenge) => {
    setMfaChallenge(challenge);
    setStatus('mfa-pending');
  }, []);

  const signOut = useCallback(async (reason) => {
    dlog('signOut', reason);
    accessRef.current = null;
    refreshRef.current = null;
    tokenStorage.clear();
    setMfaChallenge(null);
    setStatus('anonymous');
  }, []);

  const value = useMemo(
    () => ({
      status,
      mfaChallenge,
      getAccessToken: () => accessRef.current,
      getRefreshToken: () => refreshRef.current,
      setTokens,
      beginMfa,
      signOut,
    }),
    [status, mfaChallenge, setTokens, beginMfa, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
